import React, { useState } from 'react';
import { Settings, ClipboardList, MessageSquare, Zap, TrendingUp, Users, CheckSquare, Footprints } from 'lucide-react';
import WidgetGenericConfig from './WidgetGenericConfig';
import { 
  aideStatutsAPI, 
  categoriesRemarquesAPI, 
  actionsRapidesAPI, 
  statutsProgressionAPI,
  profilsUtilisateurAPI,
  statutsTachesAPI,
  etapesDossierAPI
} from '../../services/api';

const WidgetParametresGeneraux = () => {
  const [activeSection, setActiveSection] = useState('aide-statuts');

  const sections = [
    {
      id: 'aide-statuts',
      label: 'Statuts Aide',
      icon: ClipboardList,
      description: 'Statuts pour les demandes de caisse et département',
      color: 'blue'
    },
    {
      id: 'categories-remarques',
      label: 'Catégories Remarques',
      icon: MessageSquare,
      description: 'Types de remarques sur les dossiers',
      color: 'green'
    },
    {
      id: 'actions-rapides',
      label: 'Actions Rapides',
      icon: Zap,
      description: 'Boutons d\'action rapide (appel, email...)',
      color: 'orange'
    },
    {
      id: 'progression',
      label: 'Barre Progression',
      icon: TrendingUp,
      description: 'Étapes de la barre de progression dossier',
      color: 'purple'
    },
    {
      id: 'profils',
      label: 'Profils Utilisateur',
      icon: Users,
      description: 'Types de profils (admin, gestionnaire...)',
      color: 'indigo'
    },
    {
      id: 'statuts-taches',
      label: 'Statuts Tâches',
      icon: CheckSquare,
      description: 'États des tâches (à faire, terminé...)',
      color: 'cyan'
    },
    {
      id: 'etapes-dossier',
      label: 'Étapes Création',
      icon: Footprints,
      description: 'Étapes du formulaire nouveau dossier',
      color: 'pink'
    },
  ];

  const renderContent = () => {
    switch (activeSection) {
      case 'aide-statuts':
        return (
          <WidgetGenericConfig
            key="aide-statuts"
            title="Statuts des demandes d'aide"
            description="Gérez les statuts pour les demandes auprès des caisses et départements"
            api={aideStatutsAPI}
            fields={[
              { key: 'icone', label: 'Icône', type: 'emoji' },
              { key: 'couleur', label: 'Couleur', type: 'color' },
              { key: 'progress', label: 'Progression (%)', type: 'number' },
            ]}
          />
        );
      
      case 'categories-remarques':
        return (
          <WidgetGenericConfig
            key="categories-remarques"
            title="Catégories de remarques"
            description="Types de remarques pouvant être ajoutées aux dossiers"
            api={categoriesRemarquesAPI}
            fields={[
              { key: 'icone', label: 'Icône', type: 'emoji' },
              { key: 'couleur', label: 'Style de couleur', type: 'bgColor' },
            ]}
          />
        );
      
      case 'actions-rapides':
        return (
          <WidgetGenericConfig
            key="actions-rapides"
            title="Actions rapides"
            description="Boutons d'action rapide disponibles sur la fiche dossier"
            api={actionsRapidesAPI}
            fields={[
              { key: 'icone', label: 'Icône', type: 'emoji' },
              { key: 'couleur', label: 'Couleur du bouton', type: 'tailwindBg' },
              { key: 'description', label: 'Placeholder', type: 'text', placeholder: 'Texte d\'aide...' },
            ]}
          />
        );
      
      case 'progression':
        return (
          <WidgetGenericConfig
            key="progression"
            title="Barre de progression"
            description="Étapes affichées dans la barre de progression du dossier"
            api={statutsProgressionAPI}
            fields={[
              { key: 'couleur', label: 'Couleur', type: 'tailwindBg' },
              { key: 'progress', label: 'Progression (%)', type: 'number' },
            ]}
          />
        );
      
      case 'profils':
        return (
          <WidgetGenericConfig
            key="profils"
            title="Profils utilisateur"
            description="Types de profils pour les membres de l'équipe"
            api={profilsUtilisateurAPI}
            fields={[
              { key: 'couleur', label: 'Couleur', type: 'color' },
              { key: 'description', label: 'Description', type: 'text', placeholder: 'Description du profil...' },
            ]}
          />
        );
      
      case 'statuts-taches':
        return (
          <WidgetGenericConfig
            key="statuts-taches"
            title="Statuts des tâches"
            description="États possibles pour les tâches du suivi"
            api={statutsTachesAPI}
            fields={[
              { key: 'icone', label: 'Icône', type: 'emoji' },
              { key: 'couleur', label: 'Couleur', type: 'color' },
            ]}
          />
        );
      
      case 'etapes-dossier':
        return (
          <WidgetGenericConfig
            key="etapes-dossier"
            title="Étapes création dossier"
            description="Étapes du formulaire de création d'un nouveau dossier"
            api={etapesDossierAPI}
            fields={[
              { key: 'icone', label: 'Icône', type: 'emoji' },
            ]}
            labelField="title"
          />
        );
      
      default:
        return null;
    }
  };

  const getColorClasses = (color, isActive) => {
    const colors = {
      blue: isActive ? 'bg-blue-100 text-blue-700 border-blue-300' : 'hover:bg-blue-50',
      green: isActive ? 'bg-green-100 text-green-700 border-green-300' : 'hover:bg-green-50',
      orange: isActive ? 'bg-orange-100 text-orange-700 border-orange-300' : 'hover:bg-orange-50',
      purple: isActive ? 'bg-purple-100 text-purple-700 border-purple-300' : 'hover:bg-purple-50',
      indigo: isActive ? 'bg-indigo-100 text-indigo-700 border-indigo-300' : 'hover:bg-indigo-50',
      cyan: isActive ? 'bg-cyan-100 text-cyan-700 border-cyan-300' : 'hover:bg-cyan-50',
      pink: isActive ? 'bg-pink-100 text-pink-700 border-pink-300' : 'hover:bg-pink-50',
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 bg-gradient-to-br from-gray-600 to-gray-800 rounded-xl flex items-center justify-center">
          <Settings className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Paramètres Généraux</h2>
          <p className="text-sm text-gray-500">Configurez les listes et options de l'application</p>
        </div>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Sidebar */}
        <div className="col-span-4">
          <div className="bg-gray-50 rounded-xl p-4 space-y-2">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg border text-left transition ${
                    isActive 
                      ? getColorClasses(section.color, true) 
                      : 'border-transparent text-gray-600 ' + getColorClasses(section.color, false)
                  }`}
                >
                  <Icon className={`w-5 h-5 mt-0.5 ${isActive ? '' : 'text-gray-400'}`} />
                  <div>
                    <div className="font-medium">{section.label}</div>
                    <div className={`text-xs ${isActive ? 'opacity-80' : 'text-gray-400'}`}>
                      {section.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="col-span-8">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WidgetParametresGeneraux;
